import { useContext, useEffect, useMemo, useState } from "react";
import Head from "./Head";
import GlobalConfig from "../context/GlobalConfig";
import "./userDetails.css";
import { languageData } from "../constants/languageData";
import RenderSvg from "../components/RenderSvg";
import JourneyConfig from "../context/JourneyConfig";
import Footer from "../components/Footer";
import Loader from "../components/Loader/Loader";
import { noDocReplacements } from "../constants/documentSideConfigConstants";

const DL_ID_TYPE = noDocReplacements["Driving License"].storedValue;

/** DD/MM/YYYY, the format printed on an Indian driving licence and the one the RTO lookup expects. */
const DOB_PATTERN = /^(\d{2})\/(\d{2})\/(\d{4})$/;

/**
 * Formats keystrokes into DD/MM/YYYY as they are typed, so the applicant never has to place the
 * separators themselves — a mistyped one is the difference between a match and a "not found".
 * Deletions pass through untouched: re-adding a slash the applicant just deleted would trap the
 * cursor behind it.
 */
const formatDateOfBirthInput = (value, previousValue) => {
  const digits = value.replace(/\D/g, "").slice(0, 8);
  const isDeleting = value.length < previousValue.length;

  let formatted = digits;
  if (digits.length > 4) {
    formatted = `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
  } else if (digits.length > 2) {
    formatted = `${digits.slice(0, 2)}/${digits.slice(2)}`;
  }

  // While typing, close a complete field with its separator so the next digit lands in the next
  // field. While deleting, leave the trailing separator off.
  if (!isDeleting && (digits.length === 2 || digits.length === 4)) {
    formatted = `${formatted}/`;
  }
  return formatted;
};

const DOB_ERROR = {
  FORMAT: "format",
  INVALID: "invalid",
  FUTURE: "future",
};

/** languageData key and English fallback for each DOB_ERROR. */
const DOB_ERROR_MESSAGES = {
  [DOB_ERROR.FORMAT]: ["dobRequired", "Please enter your date of birth in DD/MM/YYYY format"],
  [DOB_ERROR.INVALID]: ["dobInvalid", "Please enter a valid date of birth"],
  [DOB_ERROR.FUTURE]: ["dobFuture", "Date of birth cannot be in the future"],
};

/**
 * Returns the DOB_ERROR the value fails on, or null when it is a usable date of birth. Each failure
 * gets its own message — a future date is correctly formatted, so telling the applicant to use
 * DD/MM/YYYY leaves them no way to see what is wrong.
 */
const getDateOfBirthError = (value) => {
  const match = DOB_PATTERN.exec(value);
  if (!match) return DOB_ERROR.FORMAT;

  // "31/02/2001" matches the pattern but is not a date: Date rolls it over into March.
  const [, day, month, year] = match.map(Number);
  const date = new Date(year, month - 1, day);
  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  ) {
    return DOB_ERROR.INVALID;
  }

  if (date > new Date()) return DOB_ERROR.FUTURE;
  return null;
};

const UserDetails = ({ stepperFunc, steps, currentStep, currentDocumentStep }) => {
  const { config } = useContext(GlobalConfig);
  const language = sessionStorage.getItem("language") ?? "en";
  const { branding } = config ?? {};
  const { journey, updateJourney } = useContext(JourneyConfig);

  // Which document this screen is collecting details for. SelectDocument stores the exact type it
  // sent to the backend under "document", so that is the authoritative answer and survives a
  // refresh; capturedData is the fallback for a journey whose type was prefilled and never passed
  // through the picker. currentDocumentStep is 0-based, so document 2 lives under idType2.
  const idType = useMemo(() => {
    const idTypeKey = `idType${currentDocumentStep ? currentDocumentStep + 1 : ""}`;
    return (
      sessionStorage.getItem("document") ||
      journey?.capturedData?.[idTypeKey] ||
      journey?.userInfo?.id?.[idTypeKey] ||
      ""
    );
  }, [journey, currentDocumentStep]);

  const isDrivingLicense = idType === DL_ID_TYPE;
  const isDigilocker = idType === "Digilocker" || idType === "Digilocker 2.0";

  useEffect(() => {
    if (isDigilocker) stepperFunc(steps?.[2] ?? currentStep - 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDigilocker]);

  const [formData, setFormData] = useState({
    panNumber: "",
    dlNumber: "",
    dob: "",
  });

  const text = languageData[language]?.userDetails ?? {};
  const fallbackText = languageData["en"]?.userDetails ?? {};
  const label = (key, fallback) => text[key] ?? fallbackText[key] ?? fallback;

  // Only the fields belonging to the selected document are sent — a driving licence submission
  // carrying an empty panNumber would land on the journey as an unset PAN.
  const submittedDetails = isDrivingLicense
    ? { dlNumber: formData.dlNumber.trim(), dob: formData.dob.trim() }
    : { panNumber: formData.panNumber.trim() };

  const isFormComplete = isDrivingLicense
    ? Boolean(formData.dlNumber.trim()) && Boolean(formData.dob.trim())
    : Boolean(formData.panNumber.trim());

  const updateJourneyAsync = async () => {
    await updateJourney(
      "USER_DETAILS",
      {
        userDetails: submittedDetails,
        currentDocumentStep: currentDocumentStep + 1,
      },
      currentStep
    );
    stepperFunc(steps?.[0] ?? currentStep + 1);
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: field === "dob" ? formatDateOfBirthInput(value, prev.dob) : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isDrivingLicense) {
      if (!formData.dlNumber.trim()) {
        alert(label("dlRequired", "Please enter your driving license number"));
        return;
      }
      const dobError = getDateOfBirthError(formData.dob.trim());
      if (dobError) {
        alert(label(...DOB_ERROR_MESSAGES[dobError]));
        return;
      }
    } else if (!formData.panNumber.trim()) {
      alert(label("panRequired", "Please enter your PAN number"));
      return;
    }

    sessionStorage.setItem("userDetails", JSON.stringify(submittedDetails));

    console.log("UserDetails form submitted, calling API...");

    try {
      await updateJourneyAsync();
      console.log("API call successful, moving to next step...");
    } catch (err) {
      console.error("Error updating journey:", err);
      alert(
        label("saveError", "Something went wrong while saving details. Please try again.")
      );
    }
  };

  // The Proceed button lives outside the form (it sits with the footer), so the browser gives this
  // form no implicit submission once it has more than one field — Enter did nothing on the driving
  // licence layout. Handle Enter here instead, and preventDefault so the single-field PAN layout
  // does not also submit natively and run the handler twice.
  const handleKeyDown = (e) => {
    if (e.key !== "Enter") return;
    e.preventDefault();
    handleSubmit(e);
  };

  if (isDigilocker) return <Loader />;

  return (
    <>
      <Head
        showBackIcon={!journey?.userInfo?.id?.idType}
        onNextStep={stepperFunc}
        showMobileFlow
        handleBack={() => stepperFunc(steps[2])}
        showLanguages={false}
        style={{ zIndex: "100" }}
        stepperFunc={stepperFunc}
      />

      <div className="parent-container userDetails">
        <div className="main">
          {/* Left Section - Illustration */}
          <div className="left-section">
            <div style={{ display: "block" }}>
              <RenderSvg
                config={config}
                svgType={"selectDocument"}
                id="selectDocument"
              />
            </div>
          </div>

          {/* Right Section - Document Details Form */}
          <div className="right-section">
            <div className="flex flex-col h-full justify-between">
              <div>
                <div className="text-[18px] md:text-[24px] font-semibold text-[#000000] text-center md:text-start md:mb-[32px] mb-[20px]">
                  {isDrivingLicense
                    ? label("dlHeading", "Driving License Verification")
                    : label("heading", "PAN Verification")}
                </div>

                <div className="text-[14px] md:text-[16px] text-[#656565] text-center md:text-start mb-[32px]">
                  {label("subHeading", "Please fill in the details to verify")}
                </div>

                <form
                  onSubmit={handleSubmit}
                  onKeyDown={handleKeyDown}
                  className="flex flex-col gap-6"
                >
                  {isDrivingLicense ? (
                    <>
                      <div className="inputGroup">
                        <label className="inputLabel" htmlFor="dlNumber">
                          {label("dlLabel", "Driving License Number")}
                        </label>
                        <input
                          id="dlNumber"
                          type="text"
                          className="formInput"
                          value={formData.dlNumber}
                          onChange={(e) =>
                            handleInputChange(
                              "dlNumber",
                              e.target.value.toUpperCase()
                            )
                          }
                          placeholder="KA0520170001234"
                          maxLength={20}
                        />
                      </div>
                      <div className="inputGroup">
                        <label className="inputLabel" htmlFor="dob">
                          {label("dobLabel", "Date of Birth")}
                        </label>
                        <input
                          id="dob"
                          type="text"
                          inputMode="numeric"
                          className="formInput"
                          value={formData.dob}
                          onChange={(e) =>
                            handleInputChange("dob", e.target.value)
                          }
                          placeholder="DD/MM/YYYY"
                          maxLength={10}
                        />
                      </div>
                    </>
                  ) : (
                    <div className="inputGroup">
                      <label className="inputLabel" htmlFor="panNumber">
                        {label("panLabel", "PAN Number")}
                      </label>
                      <input
                        id="panNumber"
                        type="text"
                        className="formInput"
                        value={formData.panNumber}
                        onChange={(e) =>
                          handleInputChange("panNumber", e.target.value)
                        }
                        placeholder="ABCDE1234F"
                        maxLength={10}
                      />
                    </div>
                  )}
                </form>
              </div>

              {/* ✅ Proceed button above Footer */}
              <div className="flex flex-col gap-2 mt-8">
                <button
                  type="submit"
                  onClick={handleSubmit}
                  className="proceedButton"
                  disabled={!isFormComplete}
                  aria-disabled={!isFormComplete}
                  style={{
                    backgroundColor: branding?.accentColor ?? "#1658aa",
                    opacity: !isFormComplete ? 0.5 : 1,
                    cursor: !isFormComplete ? "not-allowed" : "pointer",
                  }}
                >
                  <span>{label("proceed", "Proceed")}</span>
                  <span className="arrowIcon">→</span>
                </button>

                <Footer />
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default UserDetails;
